// SendFeedback.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

// Match AdminPanel.jsx API_BASE pattern exactly
const API_BASE =
  (typeof window !== "undefined" &&
    String(window.location.hostname).indexOf("ai-translation-frontend-sandbox") !== -1)
    ? "https://ai-translation-backend-sandbox.onrender.com"
    : "https://ai-translation-backend.onrender.com";

export default function SendFeedback() {
  const { jwt, user } = useAuth();

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  const [name, setName] = useState(fullName);
  const [email, setEmail] = useState(user?.email ? String(user.email) : "");
  const [feedback, setFeedback] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  // New: success state to swap the form for a dedicated confirmation screen
  const [success, setSuccess] = useState(false);

  // New: when success, scroll to top so the confirmation is unmistakable
  useEffect(() => {
    if (success && typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [success]);

  const handleSend = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!name.trim() || !email.trim() || !feedback.trim()) {
      setMessage("Please complete all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/support/feedback`,
        {
          name: name.trim(),
          email: email.trim(),
          feedback: feedback.trim(),
        },
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      // Clear the text area but keep name/email for convenience
      setFeedback("");
      // New: show full-page success state (prevents accidental resubmission)
      setSuccess(true);
    } catch (err) {
      setMessage("Failed to send feedback. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  // New: easy way to let users send another entry without navigating away
  const handleSendAnother = () => {
    setMessage("");
    setFeedback("");
    setSuccess(false);
  };

  // New: avoid routing assumptions; let the browser go back
  const handleGoBack = () => {
    if (typeof window !== "undefined") {
      window.history.back();
    }
  };

  // Success screen (industry-standard confirmation state)
  if (success) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-left">
        <img
          src="/Feedback.png"
          alt="Feedback sent"
          className="w-[120px] h-auto rounded-md mb-4"
        />
        <h2 className="text-2xl font-semibold mb-2">Thanks!</h2>
        <p className="text-gray-700 mb-6">
          Your feedback has been sent. We appreciate you taking the time to help us improve.
        </p>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSendAnother}
            className="px-4 py-2 rounded text-white hover:opacity-90"
            style={{ backgroundColor: "#00a7e1" }}
          >
            Send More Feedback
          </button>
          <button
            type="button"
            onClick={handleGoBack}
            className="px-4 py-2 rounded border hover:bg-gray-50"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Default: feedback form
  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-left">
      <img
        src="/Feedback.png"
        alt="Send Feedback"
        className="w-[120px] h-auto rounded-md mb-4"
      />
      <h2 className="text-xl font-semibold mb-4">Send Feedback</h2>

      <form onSubmit={handleSend} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="fb-name">
            Name:
          </label>
          <input
            id="fb-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-[#00a7e1]/40 focus:border-[#00a7e1]"
            placeholder="Your name"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="fb-email">
            Email Address:
          </label>
          <input
            id="fb-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-[#00a7e1]/40 focus:border-[#00a7e1]"
            placeholder="your@email.com"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="fb-text">
            Describe Your Feedback:
          </label>
          <textarea
            id="fb-text"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={6} // default six lines; grows with user content via scroll
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-[#00a7e1]/40 focus:border-[#00a7e1] resize-y overflow-auto"
            placeholder="Please tell us what's on your mind…"
            disabled={submitting}
          />
          <p className="text-xs text-gray-500 mt-2">
            Some account and system information may be included. We will use it to improve our services,
            subject to our Privacy Policy and Terms. We may email you for more information or updates.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 rounded text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: submitting ? "#9ca3af" : "#00a7e1" }}
          >
            {submitting ? "Sending…" : "Send"}
          </button>
          {message && (
            <span className="text-sm text-red-600" role="alert">
              {message}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
